import config from '../../../config';

import { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { push } from 'connected-react-router';
import moment from 'moment';
// i18next
import { useTranslation } from 'react-i18next';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { setCheckedQuizzesIds, setInitialField } from 'store/quiz/quizzesSlice';
import { selectCurrentUser } from 'store/currentUser/currentUserSelectors';
import { selectCheckedQuizzesIds } from 'store/quiz/quizzesSelectors';
import IQuiz from 'models/Quiz';
import UserRoles from 'types/UserRoles';
// Component
import QuizEmbedInstructionDialog from 'dialogs/QuizEmbedInstruction.dialog';
import { IconButton } from 'components/Buttons';
import { Tooltip } from 'components/Tooltip';
import QuizMenu from 'components/QuizMenu';
import { Checkbox } from 'components/Controls';
// Icons
import { FaEdit, FaEye, FaCode, FaRegBuilding, FaRegUser, FaRegClock } from 'react-icons/fa';
import { MdWarningAmber, MdAccountTree } from "react-icons/md";
// Utilities
import { quizTypesLabels } from 'utilities/utilities';

type Props = {
  quiz: IQuiz;
}

const QuizzesListItem:FC<Props> = ({
  // Props
  quiz
}) => {
  const { t } = useTranslation();

  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const currentUser = useAppSelector(selectCurrentUser);
  const checkedQuizzesIds = useAppSelector(selectCheckedQuizzesIds);

  const [ dialogQuizEmbedInstructionOpen, setDialogQuizEmbedInstructionOpen ] = useState<boolean>(false);

  const toggleQuizEmbedInstructionDialog = () => setDialogQuizEmbedInstructionOpen((prevState:boolean) => !prevState);

  const handleGoTo = (url:string) => dispatch(push((url)));

  const handlePreview = () => {
    window.open(`${config.quizURL}/quiz/${quiz.quizNameSlug}?u=${new Date().getTime()}&preview=true`);
  };

  const isAdmin = currentUser?.groups.includes(UserRoles.Admin);
  const role = isAdmin ? 'admin' : 'owner';

  const handleCheckQuiz = () => {
    const nextQuizzesIds = checkedQuizzesIds.includes(quiz._id)
      ? checkedQuizzesIds.filter((id:string) => id !== quiz._id)
      : [ ...checkedQuizzesIds, quiz._id ];
    dispatch(setCheckedQuizzesIds(nextQuizzesIds));
  }

  const checked = useMemo(() => checkedQuizzesIds.includes(quiz._id), [ checkedQuizzesIds, quiz._id ]);

  useEffect(() => {
    return () => {
      dispatch(setInitialField('checkedQuizzesIds'));
    }
    // eslint-disable-next-line
  }, []);

  return (
    <Fragment>
      <tr>
        <td className="text-left title flex gap-2 items-center">
          {isAdmin ? (
            <Checkbox
              id={`check-quiz-${quiz._id}`}
              name={`check-quiz-${quiz._id}`}
              checked={checked}
              onChange={handleCheckQuiz}
              label=""
              className="mb-0"
            />
          ) : null}
          {!quiz.questions.length && (
            <Tooltip title={t('pages.quizzes.questionsHint')}>
              <MdWarningAmber color="orange" />
            </Tooltip>
          )}
          {quiz.quizName}
          </td>
        <td className="text-left text capitalize">{quizTypesLabels[quiz.type]}</td>
        <td className="text-left text">
          <FaRegBuilding className="icon" size="16px" />
          {quiz.namespace.slug}
        </td>
        <td className="text-left text">
          <FaRegUser className="icon" size="16px" />
          {(quiz as any).createdByName}
        </td>
        <td className="text-left text">
          <FaRegClock className="icon" size="16px" />
          {moment(quiz.createdAt).format('MMM Do YYYY')}
        </td>
        <td className="text-right actions">
          <Tooltip title={t('pages.quizzes.editButton')}>
            <IconButton
              onClick={() => handleGoTo(`/${role}/quizzes/${quiz._id}`)}
            ><FaEdit /></IconButton>
          </Tooltip>
          <Tooltip className="ml-1" title={t('pages.quizzes.editPagesButton')}>
            <IconButton
              onClick={() => handleGoTo(`/${role}/quizzes/${quiz._id}?tab=pages`)}
            ><MdAccountTree /></IconButton>
          </Tooltip>
          <Tooltip className="ml-1" title={t('pages.quizzes.previewButton')}>
            <IconButton
              onClick={handlePreview}
            ><FaEye /></IconButton>
          </Tooltip>
          {!isAdmin && (
            <Tooltip className="ml-1" title={t('pages.quizzes.embedInstructionButton')}>
              <IconButton
                onClick={toggleQuizEmbedInstructionDialog}
              ><FaCode /></IconButton>
            </Tooltip>
          )}
          <QuizMenu quiz={quiz} />
        </td>
      </tr>
      {dialogQuizEmbedInstructionOpen ? (
        <QuizEmbedInstructionDialog
          quizNameSlug={quiz.quizNameSlug}
          quizNamespaceSlug={quiz.namespace.slug}
          open={dialogQuizEmbedInstructionOpen}
          onClose={toggleQuizEmbedInstructionDialog}
        />
      ) : null}
    </Fragment>
  )
}

export default QuizzesListItem;
